import { z } from 'zod';

const MAX_TO_EMAILS = 5;
const MAX_CC_EMAILS = 5;
const MAX_EMAIL_MESSAGE_LENGTH = 500;

const emailSchema = z.object({
    label: z.string(),
    value: z.string().email(),
});

const emailSelectFormSchema = ({
    fromEmailRequired = false, //
}: {
    fromEmailRequired: boolean;
}) => z
    .object({
        fromEmail: z.string().email().optional(),
        emailFooter: z.string().optional(),
        toEmails: z
            .array(emailSchema)
            .min(1, 'At least one email is required')
            .max(MAX_TO_EMAILS, `A maximum of ${MAX_TO_EMAILS} To emails are allowed`),
        ccEmails: z
            .array(emailSchema)
            .max(MAX_CC_EMAILS, `A maximum of ${MAX_CC_EMAILS} CC emails are allowed`),
        emailMessage: z
            .string(),
        sendToDefaultRecipient: z.boolean(),
        defaultRecipient: z.string().email().optional(),
    })
    .superRefine((val, ctx) => {
        const toEmails = val.toEmails.map((email) => email.value);
        const ccEmails = val.ccEmails.map((email) => email.value);

        const duplicatedEmails = toEmails.filter((email) => ccEmails.includes(email));

        if (duplicatedEmails.length > 0) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['ccEmails'],
                message: `One or more email addresses are already included in the "To" section (${duplicatedEmails.join(
                    ', '
                )})`,
            });
        }

        if (fromEmailRequired && (val.fromEmail == null || val.fromEmail === '' || val.fromEmail === undefined)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['fromEmail'],
                message: 'A "From" email is required',
            });
        }

        if (val.emailMessage != null) {
            let trimmed = val.emailMessage
                .replace(/<[^>]*(>|$)|&nbsp;/g, '') // trim nbsp
                .replace(/(<([^>]+)>)/gi, '') // trim html tags
                .replace(/(?:\r\n|\r|\n)/g, ''); // trim line break

            if (trimmed.length >= MAX_EMAIL_MESSAGE_LENGTH) {
                ctx.addIssue({
                    code: z.ZodIssueCode.custom,
                    path: ['emailMessage'],
                    message: `Message should be ${MAX_EMAIL_MESSAGE_LENGTH} characters or less`,
                });
            }
        }
    })
    .transform((val) => {
        if (!val.sendToDefaultRecipient) return val;

        const isDefaultRecipientIncluded = val.toEmails.some(
            (email) => email.value === val.defaultRecipient
        )
            || val.ccEmails.some((email) => email.value === val.defaultRecipient);

        if (isDefaultRecipientIncluded) return val;

        return {
            ...val,
            ccEmails: [
                ...val.ccEmails,
                {
                    label: val.defaultRecipient as string,
                    value: val.defaultRecipient as string,
                },
            ],
        };
    });

type ForwardEmailFormValues = z.infer<ReturnType<typeof emailSelectFormSchema>>;

export { emailSelectFormSchema, ForwardEmailFormValues };
